import React from 'react'
import { TimerTheme, TimerThemeProps } from '../types'
import Snowfall from './Snowfall'
import { CandyCaneFrame } from './CandyCaneDecor'

/**
 * 聖誕主題組件 - 包含下雪效果和拐杖糖裝飾
 */
const ChristmasThemeComponent: React.FC<TimerThemeProps> = ({ children, isRunning = false }) => {
  return (
    <div className="relative">
      {/* 下雪效果 - 全螢幕 */}
      <Snowfall />
      
      {/* 主要內容區域 */}
      <div className="relative">
        {/* 聖誕裝飾背景 */}
        <div className="absolute inset-0 rounded-lg md:rounded-2xl overflow-hidden pointer-events-none">
          {/* 聖誕配色背景 */}
          <div className={`
            absolute inset-0 transition-all duration-500
            ${isRunning 
              ? 'bg-gradient-to-br from-red-50/80 via-green-50/60 to-red-50/80' 
              : 'bg-gradient-to-br from-red-50/40 via-white/60 to-green-50/40'
            }
          `} />
          
          {/* 拐杖糖裝飾框架 */}
          <CandyCaneFrame />
          
          {/* 聖誕燈飾效果 - 只在運行時顯示 */}
          {isRunning && (
            <div className="absolute top-0 left-0 right-0 flex justify-around p-2">
              <div className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-red-500 animate-pulse" style={{ animationDelay: '0s' }} />
              <div className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-green-500 animate-pulse" style={{ animationDelay: '0.5s' }} />
              <div className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-yellow-400 animate-pulse" style={{ animationDelay: '1s' }} />
              <div className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-blue-500 animate-pulse" style={{ animationDelay: '1.5s' }} />
              <div className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-red-500 animate-pulse" style={{ animationDelay: '2s' }} />
            </div>
          )}
          
          {/* 雪堆效果 - 底部 */}
          <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-white/60 to-transparent" />
          
          {/* 聖誕星星裝飾 - 四角 */}
          <div className="absolute top-2 left-2 text-yellow-400 opacity-70 animate-pulse">
            ⭐
          </div>
          <div className="absolute top-2 right-2 text-yellow-400 opacity-70 animate-pulse" style={{ animationDelay: '1s' }}>
            ⭐
          </div>
        </div>
        
        {/* 實際內容 */}
        <div className="relative z-10">
          {children}
        </div>
      </div>
      
      {/* 聖誕主題專用樣式 */}
      <style>{`
        @keyframes twinkle {
          0%, 100% {
            opacity: 0.7;
            transform: scale(1);
          }
          50% {
            opacity: 1;
            transform: scale(1.2);
          }
        }
      `}</style>
    </div>
  )
}

/**
 * 聖誕主題定義
 */
const ChristmasTheme: TimerTheme = {
  config: {
    name: 'christmas',
    displayName: '聖誕主題 🎄',
    colors: {
      runningPrimary: 'text-red-600',
      runningSecondary: 'bg-red-500',
      runningAccent: 'bg-red-400',
      stoppedPrimary: 'text-green-700',
      stoppedSecondary: 'bg-green-600',
      stoppedAccent: 'bg-green-500',
      runningBackground: 'bg-gradient-to-br from-red-50 to-green-50',
      stoppedBackground: 'bg-gradient-to-br from-red-50/50 to-green-50/50',
    },
    hasSpecialEffects: true,
  },
  Component: ChristmasThemeComponent,
}

export default ChristmasTheme
